// src/components/ui/lists/PartnersListView/PartnersListViewShadcn.jsx
import React, { useState } from 'react';
import {
  Search,
  Users,
  Handshake,
  TrendingUp,
  Building2,
  Grid3X3,
  List,
  Filter
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardHeader, CardTitle } from '../../card';
import { Input } from '../../input';
import { Button } from '../../button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../select';
import { Badge } from '../../badge';

// Import shadcn partner card (will create this next)
import PartnerCardShadcn from '../../cards/PartnerCard/PartnerCardShadcn';

import { cn } from '../../../../utils/cn';

const PartnersListViewShadcn = ({
  partners = [],
  onMessage,
  onViewDetails,
  onDisconnect,
  loading = false,
  stats = {},
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'date', 'location'

  // Filter and sort partners
  const filteredPartners = React.useMemo(() => {
    let filtered = partners;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(partner =>
        partner.name?.toLowerCase().includes(search) ||
        partner.contact_email?.toLowerCase().includes(search) ||
        partner.network_profile?.profile_description?.toLowerCase().includes(search) ||
        partner.address?.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'date':
          const dateA = new Date(a.connected_at || a.created_at || 0);
          const dateB = new Date(b.connected_at || b.created_at || 0);
          return dateB - dateA; // Most recent first
        case 'location':
          return (a.address || '').localeCompare(b.address || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [partners, searchTerm, sortBy]);

  const statsCards = [
    {
      title: "Total Partners",
      value: partners.length,
      icon: Users,
      color: "text-blue-500"
    },
    {
      title: "Active Connections",
      value: stats.activeConnections || partners.length,
      icon: Handshake,
      color: "text-emerald-500"
    },
    {
      title: "This Month Referrals",
      value: stats.monthlyReferrals || 0,
      icon: TrendingUp,
      color: "text-purple-500"
    },
    {
      title: "Business Types",
      value: stats.businessTypes || new Set(partners.map(p => p.business_type)).size,
      icon: Building2,
      color: "text-amber-500"
    }
  ];

  if (loading) {
    return (
      <div className={cn("space-y-6", className)}>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (partners.length === 0) {
    return (
      <div className={cn("space-y-6", className)}>
        <Card>
          <CardContent className="p-6 text-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Partners Yet</h3>
            <p className="text-muted-foreground">Start building your network by connecting with other businesses.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header with Stats */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Partner Network
              </CardTitle>
              <Badge variant="secondary">
                {partners.length} Partner{partners.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {statsCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className={cn("w-6 h-6 mx-auto mb-2", stat.color)} />
                  <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.title}</div>
                </div>
              );
            })}
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search partners..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="date">Date Added</SelectItem>
                  <SelectItem value="location">Location</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex rounded-lg border">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="rounded-r-none"
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Partners Grid/List */}
      {filteredPartners.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center py-12">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No matches found</h3>
            <p className="text-muted-foreground">Try adjusting your search criteria.</p>
          </CardContent>
        </Card>
      ) : (
        <div className={cn(
          viewMode === 'grid'
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-4"
        )}>
          {filteredPartners.map((partner) => (
            <PartnerCardShadcn
              key={partner.id}
              partner={partner}
              viewMode={viewMode}
              onMessage={() => onMessage && onMessage(partner.id, partner.name)}
              onViewDetails={() => onViewDetails && onViewDetails(partner.id)}
              onDisconnect={() => onDisconnect && onDisconnect(partner.id, partner.name)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnersListViewShadcn;